import { useState, useEffect, useCallback } from 'react'
import type { RowDragEndEvent, GridApi, RowNode } from 'ag-grid-community'
import {
  getMatchOrderings,
  saveOrderings,
  applySavedOrdering,
  generateOrderingMap,
  clearMatchOrderings,
} from '../utils/taxTransactionOrdering'

interface UseTaxTransactionOrderingProps {
  matchId: string
  type: 'bol' | 'invoice'
}

interface UseTaxTransactionOrderingReturn<T> {
  /**
   * Apply saved ordering to a list of transactions
   */
  applyOrdering: (transactions: T[]) => T[]
  /**
   * Save the current order of transactions
   */
  saveOrder: (transactions: T[]) => void
  /**
   * Handle row reorder event (for AG Grid)
   */
  handleRowDragEnd: (event: RowDragEndEvent) => void
  /**
   * Clear saved ordering for this match/type
   */
  clearOrdering: () => void
  /**
   * Current saved orderings
   */
  savedOrderings: Record<string, number>
}

export function useTaxTransactionOrdering<T extends { id: string }>({
  matchId,
  type,
}: UseTaxTransactionOrderingProps): UseTaxTransactionOrderingReturn<T> {
  const [savedOrderings, setSavedOrderings] = useState<Record<string, number>>({})

  // Load saved orderings on mount and when matchId changes
  useEffect(() => {
    if (!matchId) return

    const orderings = getMatchOrderings(matchId)
    setSavedOrderings(orderings[type] || {})
  }, [matchId, type])

  // Apply saved ordering to transactions
  const applyOrdering = useCallback(
    (transactions: T[]): T[] => {
      if (!matchId || !transactions || transactions.length === 0) {
        return transactions
      }

      return applySavedOrdering(transactions, savedOrderings)
    },
    [matchId, savedOrderings]
  )

  // Save the current order of transactions
  const saveOrder = useCallback(
    (transactions: T[]) => {
      if (!matchId || !transactions || transactions.length === 0) return

      const orderingMap = generateOrderingMap(transactions)
      saveOrderings(matchId, type, orderingMap)
      setSavedOrderings(orderingMap)
    },
    [matchId, type]
  )

  // Handle AG Grid row drag end event
  const handleRowDragEnd = useCallback(
    (event: RowDragEndEvent) => {
      if (!matchId || !event.api) return

      // Get all rows in their current order
      const allRows: T[] = []
      event.api.forEachNodeAfterFilterAndSort((node: RowNode) => {
        if (node.data) {
          allRows.push(node.data)
        }
      })

      // Save the new order
      saveOrder(allRows)
    },
    [matchId, saveOrder]
  )

  // Clear saved ordering
  const clearOrdering = useCallback(() => {
    if (!matchId) return

    const orderings = getMatchOrderings(matchId)
    orderings[type] = {}
    saveOrderings(matchId, type, {})
    setSavedOrderings({})
  }, [matchId, type])

  return {
    applyOrdering,
    saveOrder,
    handleRowDragEnd,
    clearOrdering,
    savedOrderings,
  }
}
